import {
  Editor,
  Transforms,
  Text,
  Range,
  Node,
  Element as SlateElement,
  type Descendant,
  type Point,
} from 'slate';
import type { SlateEditorType, CustomText } from '@builder4/shared/types/text';
import type { LinkVariant, StylesConfig } from '@builder4/shared/types/styles';
import type { RGBA } from '@builder4/shared/types/layout';

export function applyMultipleMarksToAll(editor: SlateEditorType, style: Partial<CustomText>) {
  if (!editor) return;

  const start = Editor.start(editor, []);
  const end = Editor.end(editor, []);

  Transforms.select(editor, { anchor: start, focus: end });
  for (const [, path] of Editor.nodes(editor, {
    at: [],
    match: Text.isText,
  })) {
    Transforms.setNodes(editor, { ...style }, { at: path });
  }
}
export function applyMarkToAll<T = unknown>(
  editor: SlateEditorType,
  markKey: string,
  markValue: T,
) {
  if (!editor) return;

  const start = Editor.start(editor, []);
  const end = Editor.end(editor, []);

  Transforms.select(editor, { anchor: start, focus: end });

  for (const [, path] of Editor.nodes(editor, {
    at: [],
    match: Text.isText,
  })) {
    Transforms.setNodes(editor, { [markKey]: markValue }, { at: path });
  }
}
export function applyMark<T = unknown>(editor: SlateEditorType, markKey: string, markValue: T) {
  if (!editor) return;

  const { selection } = editor;

  if (!selection || Range.isCollapsed(selection)) {
    // No text selected: apply to all text nodes (entire node)
    if (markValue === undefined) {
      Transforms.unsetNodes(editor, [markKey], { at: [], match: Text.isText });
      return;
    }
    for (const [, path] of Editor.nodes(editor, {
      at: [],
      match: Text.isText,
    })) {
      Transforms.setNodes(editor, { [markKey]: markValue }, { at: path });
    }
  } else if (selection) {
    // Text is selected: apply as mark to selection
    if (markValue === undefined) {
      Editor.removeMark(editor, markKey);
      return;
    }
    Editor.addMark(editor, markKey, markValue);
  } else {
    // Selection is null (editor blurred): apply to all text nodes
    for (const [, path] of Editor.nodes(editor, {
      at: [],
      match: Text.isText,
    })) {
      Transforms.setNodes(editor, { [markKey]: markValue }, { at: path });
    }
  }
}

/**
 * Validates the editor's current selection is safe to use for mark reads.
 *
 * A null selection is considered valid because Slate's Editor.marks() handles it
 * gracefully. The guard only fires when a non-null selection has paths that no
 * longer exist in the document tree, which can happen after a document restructure
 * leaves the selection pointing at removed nodes.
 * @param editor
 * @returns true if the editor's current selection is safe to use for mark reads, false otherwise.
 */
function isSelectionValid(editor: SlateEditorType): boolean {
  const { selection } = editor;
  if (!selection) return true;
  if (!Array.isArray(selection.anchor?.path) || !Array.isArray(selection.focus?.path)) {
    return false;
  }
  return Node.has(editor, selection.anchor.path) && Node.has(editor, selection.focus.path);
}

export function getMark(editor: SlateEditorType, markKey: string) {
  if (!isSelectionValid(editor)) {
    return getFirstTextNodeMark(editor, markKey as keyof CustomText);
  }
  const marks = Editor.marks(editor);
  const mark = marks?.[markKey as keyof typeof marks];
  return mark;
}

export function getFirstTextNodeMark(editor: SlateEditorType, markKey: keyof CustomText) {
  for (const [node] of Editor.nodes(editor, { at: [], match: Text.isText })) {
    const textNode = node as CustomText;
    if (textNode?.[markKey] !== undefined) {
      return textNode[markKey];
    }
  }
  return undefined;
}

export function getCurrentMarkValue(editor: SlateEditorType, markKey: keyof CustomText) {
  if (!isSelectionValid(editor)) {
    return getFirstTextNodeMark(editor, markKey);
  }
  const marks = Editor.marks(editor);
  if (marks && marks[markKey as keyof typeof marks] !== undefined) {
    return marks[markKey as keyof typeof marks];
  }
  return getFirstTextNodeMark(editor, markKey);
}

export function applyMarksToAllPreservingOverrides(
  editor: SlateEditorType,
  baseStyle: Partial<CustomText>,
  stylesConfig: StylesConfig,
) {
  if (!editor) return;
  const overrideFlagsByPath = new Map<
    string,
    {
      fontWeight: boolean;
      fontStyle: boolean;
      textDecoration: boolean;
      textTransform: boolean;
      letterSpacing: boolean;
      color: boolean;
      textSolid: boolean;
      textGradient: boolean;
    }
  >();

  for (const [node, path] of Editor.nodes(editor, {
    at: [],
    match: Text.isText,
  })) {
    const textNode = node as CustomText;
    overrideFlagsByPath.set(path.join('.'), {
      fontWeight: textNode.fontWeight != null,
      fontStyle: textNode.fontStyle != null,
      textDecoration: textNode.textDecoration != null,
      textTransform: textNode.textTransform != null,
      letterSpacing: textNode.letterSpacing != null,
      color: textNode.color != null,
      textSolid: textNode.textSolid != null,
      textGradient: textNode.textGradient != null,
    });
  }

  for (const [, path] of Editor.nodes(editor, {
    at: [],
    match: Text.isText,
  })) {
    const overrideFlags = overrideFlagsByPath.get(path.join('.'));
    const nextStyle: Partial<CustomText> = { ...baseStyle };

    if (overrideFlags?.fontWeight) {
      delete nextStyle.fontWeight;
    }
    if (overrideFlags?.fontStyle) {
      delete nextStyle.fontStyle;
    }
    if (overrideFlags?.textDecoration) {
      delete nextStyle.textDecoration;
    }
    if (overrideFlags?.textTransform) {
      delete nextStyle.textTransform;
    }
    if (overrideFlags?.letterSpacing) {
      delete nextStyle.letterSpacing;
    }
    if (overrideFlags?.color) {
      delete nextStyle.color;
    }
    if (overrideFlags?.textSolid) {
      delete nextStyle.textSolid;
    }
    if (overrideFlags?.textGradient) {
      delete nextStyle.textGradient;
    }

    Transforms.setNodes(editor, nextStyle, { at: path });
  }

  for (const [node, path] of Editor.nodes(editor, {
    at: [],
    match: Text.isText,
  })) {
    const textNode = node as CustomText;
    if (!textNode.selectedLinkVariant) {
      continue;
    }

    const linkStyle = getGlobalLinkStyle(stylesConfig, textNode.selectedLinkVariant);
    const nextLinkStyle: Partial<CustomText> = { ...linkStyle };
    const overrideFlags = overrideFlagsByPath.get(path.join('.'));

    if (overrideFlags?.fontWeight) {
      delete nextLinkStyle.fontWeight;
    }
    if (overrideFlags?.textDecoration) {
      delete nextLinkStyle.textDecoration;
    }
    if (overrideFlags?.color) {
      delete nextLinkStyle.color;
    }
    if (overrideFlags?.textSolid) {
      delete nextLinkStyle.textSolid;
    }
    if (overrideFlags?.textGradient) {
      delete nextLinkStyle.textGradient;
    }

    Transforms.setNodes(editor, nextLinkStyle, { at: path });
  }
}

export function getGlobalLinkStyle(stylesConfig: StylesConfig, selectedLinkVariant: LinkVariant) {
  const linkStyles = stylesConfig.typography.link;
  const linkStyle = linkStyles[selectedLinkVariant];
  if (!linkStyle) {
    return {};
  }
  const leafNodeStyle: Partial<CustomText> = {
    color: linkStyle.fontColor as RGBA,
    textSolid: linkStyle.fontColor as RGBA,
    // backgroundColor: linkStyle.backgroundColor as RGBA, for now we do not handle background on leaf nodes.
    fontWeight: linkStyle.fontWeight,
    textDecoration: linkStyle.decoration,
  };
  return leafNodeStyle;
}

/**
 * Result of diffing old text with new text (single contiguous change).
 * Represents: delete oldText[replaceStart..replaceEnd), insert `inserted` at replaceStart.
 */
export interface TextDiff {
  replaceStart: number;
  replaceEnd: number;
  inserted: string;
}

/**
 * Computes a single contiguous text diff between oldText and newValue.
 * Finds common prefix and suffix so the "middle" is one replace segment.
 * Used to apply minimal edits in Slate and preserve existing marks.
 */
export function getTextDiff(oldText: string, newValue: string): TextDiff | null {
  if (oldText === newValue) {
    return null;
  }

  const oldLen = oldText.length;
  const newLen = newValue.length;

  // Common prefix length
  let prefixLen = 0;
  while (prefixLen < oldLen && prefixLen < newLen && oldText[prefixLen] === newValue[prefixLen]) {
    prefixLen += 1;
  }

  // Common suffix length (after the prefix)
  let suffixLen = 0;
  while (
    prefixLen + suffixLen < oldLen &&
    prefixLen + suffixLen < newLen &&
    oldText[oldLen - 1 - suffixLen] === newValue[newLen - 1 - suffixLen]
  ) {
    suffixLen += 1;
  }

  const replaceStart = prefixLen;
  const replaceEnd = oldLen - suffixLen;
  const inserted = newValue.slice(prefixLen, newLen - suffixLen);

  return { replaceStart, replaceEnd, inserted };
}

/**
 * Maps a flat character offset (in the string produced by slateValueToText)
 * to a Slate Point. Matches helperFunc.slateValueToText semantics:
 * each element's children text concatenated, elements joined by '\n'.
 */
export function getPointAtCharacterOffset(editor: Editor, targetOffset: number): Point | null {
  const children = editor.children as Descendant[];
  if (!children.length) return null;

  let offset = 0;

  for (let i = 0; i < children.length; i++) {
    const node = children[i];
    // Top-level nodes are always CustomElement (paragraph blocks).
    // Non-element nodes at root level are not expected in this editor.
    if (!SlateElement.isElement(node)) continue;

    const path = [i];

    for (let j = 0; j < node.children.length; j++) {
      const textNode = node.children[j];
      const textLen = 'text' in textNode ? String(textNode.text).length : 0;

      if (targetOffset >= offset && targetOffset <= offset + textLen) {
        return { path: [...path, j], offset: targetOffset - offset };
      }
      offset += textLen;
    }

    // Between blocks: newline character in the flat string
    if (i < children.length - 1) {
      if (targetOffset === offset) {
        return Editor.start(editor, [i + 1]);
      }
      offset += 1;
    }
  }

  return Editor.end(editor, []);
}

/**
 * Applies a text diff to the Slate editor via delete + insert, preserving
 * existing document structure and marks. Newly inserted text receives
 * the marks at the insert position.
 * Persistence is handled by Slate's onChange → useSlateTextStyles.handleChange.
 */
export function applyTextDiffToEditor(
  editor: Editor,
  currentText: string,
  newValue: string,
): boolean {
  const diff = getTextDiff(currentText, newValue);
  if (!diff) return false;

  const { replaceStart, replaceEnd, inserted } = diff;

  // Fall back to full replacement when the inserted text contains a newline.
  // Transforms.insertText places a literal '\n' inside a single text leaf
  // rather than splitting the paragraph, which breaks slateValueToText
  // round-trips and can cause rendering artifacts.
  if (inserted.includes('\n')) return false;

  const anchor = getPointAtCharacterOffset(editor, replaceStart);
  const focus = getPointAtCharacterOffset(editor, replaceEnd);
  if (!anchor || !focus) return false;

  // Clear pending marks so insertText inherits from the leaf node at the
  // insertion point rather than using stale marks from a prior interaction.
  editor.marks = null;

  Editor.withoutNormalizing(editor, () => {
    if (replaceStart < replaceEnd) {
      Transforms.delete(editor, { at: { anchor, focus } });
    }
    if (inserted.length > 0) {
      // When no text was deleted, anchor is still valid and we can reuse it
      // directly instead of recomputing. After a delete the tree structure
      // changes, so we must recompute.
      const insertPoint =
        replaceStart === replaceEnd ? anchor : getPointAtCharacterOffset(editor, replaceStart);
      if (insertPoint) {
        Transforms.insertText(editor, inserted, { at: insertPoint });
      }
    }
  });

  return true;
}
